import type { OperationLogEntry } from "@services/drybulkCargoOperationsApi";
import type { TFunction } from "i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.operation-log`;

const KNOWN_PARTIES: readonly string[] = ["OPERATIONS", "SHIPPING_AGENT", "SHIPPER"];

type Params = Record<string, string | number>;

const paramBuilders: Record<string, (entry: OperationLogEntry) => Params | null | undefined> = {
  OPERATION_CREATED: () => undefined,
  OPERATION_SUBMITTED: () => undefined,
  OPERATION_REJECTED: () => undefined,
  OPERATION_RESUBMITTED: () => undefined,
  OPERATION_APPROVED: () => undefined,
  OPERATION_SHIPPERS_READY: () => undefined,
  OPERATION_STARTED: () => undefined,
  OPERATION_AWAITING_FINAL_DRAFT_SURVEY: () => undefined,
  OPERATION_COMPLETED: () => undefined,
  OPERATION_DELETED: () => undefined,
  SHIPPER_READINESS_REQUESTED: (entry) => withCode(entry),
  SHIPPER_READY: (entry) => withCode(entry),
  SEQUENCE_UPCOMING: (entry) => withSequence(entry),
  SEQUENCE_STARTED: (entry) => withSequence(entry),
  SEQUENCE_COMPLETED: (entry) => withSequence(entry),
  TOLERANCE_CHANGED: (entry) => {
    const { shipperCompanyCode, oldTolerancePct, newTolerancePct } = entry;
    if (!shipperCompanyCode || oldTolerancePct == null || newTolerancePct == null) {
      return null;
    }
    return { code: shipperCompanyCode, old: String(oldTolerancePct), new: String(newTolerancePct) };
  },
};

function withCode({ shipperCompanyCode }: OperationLogEntry): Params | null {
  return shipperCompanyCode ? { code: shipperCompanyCode } : null;
}

function withSequence({ sequenceNo }: OperationLogEntry): Params | null {
  return sequenceNo == null ? null : { n: sequenceNo };
}

export const operationLogActionText = (t: TFunction, entry: OperationLogEntry): string => {
  const build = Object.prototype.hasOwnProperty.call(paramBuilders, entry.action)
    ? paramBuilders[entry.action]
    : undefined;
  const params = build?.(entry);
  if (!build || params === null) {
    return entry.label;
  }
  const key = `${L}.actions.${entry.action}`;
  return params ? t(key, params) : t(key);
};

export const operationLogActorText = (t: TFunction, entry: OperationLogEntry): string => {
  const user = entry.createdBy ?? entry.changedBy;
  if (user) {
    return user;
  }
  if (!entry.actorParty) {
    return "-";
  }
  return KNOWN_PARTIES.includes(entry.actorParty)
    ? t(`${L}.actor-party.${entry.actorParty}`)
    : entry.actorParty;
};
